
































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { LineChart } from "vue-chart-3";
import { MiraTrendSeries } from "@/data/models/Mira";
import {
  COLOR_MIRA_TREND_BAND_FILL,
  COLOR_MIRA_TREND_BAND_FILL_DARK,
  COLOR_MIRA_TREND_LINE,
  COLOR_MIRA_TREND_LINE_DARK,
  COLOR_MIRA_TREND_MEDIAN,
  COLOR_MIRA_TREND_MEDIAN_DARK,
  COLOR_MIRA_TREND_PERIOD,
  COLOR_MIRA_TREND_PERIOD_DARK,
  COLOR_STANDARD_WHITE,
  COLOR_SURFACE_DARK,
} from "@/utils/colors";
import { MiraViewContext } from "@/utils/MiraDataQualityView";
import { caseCountIsValue, casesText } from "@/utils/MiraCaseUnits";
import { MiraTrendGrainModule } from "@/store/modules/MiraTrendGrainModule";
import {
  MIRA_TREND_GRAINS,
  MiraTrendGrain,
  MiraTrendPointView,
  MiraTrendRange,
  MiraTrendView,
  buildTrend,
  grainForRange,
  isTotalMetric,
  pointsInRange,
  trendHasMedian,
  trendMedianValue,
} from "@/utils/MiraTrendView";
import { MiraValueFormatter, chartAxisKey, trendValueAxis } from "@/utils/MiraTrendChart";
import { themed } from "@/utils/Theme";

/**
 * The Verlauf (timeline) card: a neutral line chart of one Mira figure over time, with a Monat /
 * Quartal / Jahr switch. The colours carry no good-or-bad meaning (a rise over time is neither); the
 * line breaks over the periods that had nothing (`spanGaps: false`), the current year to date is drawn
 * hollow, and the P15 to P85 band is shown only when the screen allows percentiles and the points carry
 * them. The data comes from {@link buildTrend}, which is pure and unit-tested; this component only draws it.
 *
 * The screen hides the card when the document has no timeline for the figure; here an all-gap grain
 * shows the empty note instead of a chart.
 */
@Component({
  components: {
    "line-chart": LineChart,
  },
})
export default class MiraTrendCardComponent extends Vue {
  /** The document's timelines; the card reads the entry for {@link metricId}. */
  @Prop({ default: null })
  trends!: Record<string, MiraTrendSeries> | undefined;

  @Prop({ required: true })
  metricId!: string;

  @Prop({ required: true })
  title!: string;

  /** Formats a point's value in the figure's own unit (days, a share, a count), from the screen. */
  @Prop({ required: true })
  formatValue!: MiraValueFormatter;

  @Prop({ default: false })
  showPercentiles!: boolean;

  /** The screen's view context, for the locale month names and the number formatting. */
  @Prop({ required: true })
  context!: MiraViewContext;

  /**
   * The period the screen's header shows. The history stays fixed; this period is shaded on it, in the
   * header's own grey, so the two read as one (Taimoor, 2026-10-02).
   */
  @Prop({ default: null })
  selectedRange!: MiraTrendRange | null;

  grains = MIRA_TREND_GRAINS;

  /** The grain shown: the one choice shared by every Mira timeline card (`MiraTrendGrainModule`). */
  get grain(): MiraTrendGrain {
    return MiraTrendGrainModule.grain;
  }

  set grain(grain: MiraTrendGrain) {
    MiraTrendGrainModule.setGrain(grain);
  }

  /**
   * A chosen period outside the current view (2023, before the 24 months) switches to the view that
   * shows it: the year view. A period inside it keeps the user's grain.
   */
  @Watch("selectedRange", { immediate: true })
  onSelectedRange() {
    this.grain = grainForRange(this.trends ?? undefined, this.metricId, this.grain, this.selectedRange, this.context);
  }

  /** The points inside the chosen period, for the shading. */
  get periodSpan(): { first: number; last: number } | null {
    return pointsInRange(this.points, this.selectedRange);
  }

  /** "Okt 24 bis Sep 26, unabhängig vom gewählten Zeitraum". */
  get rangeNote(): string | null {
    const points = this.points;
    if (points.length === 0) {
      return null;
    }
    return this.$t("mira.trend.rangeNote", [points[0].label, points[points.length - 1].label]).toString();
  }

  /** Whether this figure is a total, drawn as bars with no median line and no band. */
  get isTotal(): boolean {
    return isTotalMetric(this.metricId);
  }

  /** Whether the thin median line is drawn: a figure with medians that is not a total (shown to everyone). */
  get hasMedianLine(): boolean {
    return !this.isTotal && trendHasMedian(this.points);
  }

  get medianColor(): string {
    return themed(COLOR_MIRA_TREND_MEDIAN, COLOR_MIRA_TREND_MEDIAN_DARK);
  }

  get view(): MiraTrendView {
    return buildTrend(this.trends, this.metricId, this.grain, this.formatValue, this.context);
  }

  get points(): MiraTrendPointView[] {
    return this.view.points;
  }

  get lineColor(): string {
    return themed(COLOR_MIRA_TREND_LINE, COLOR_MIRA_TREND_LINE_DARK);
  }

  get surfaceColor(): string {
    return themed(COLOR_STANDARD_WHITE, COLOR_SURFACE_DARK);
  }

  /** The chart's own wrapper fills the card's chart box, which sets the height. */
  get chartStyles(): Record<string, string> {
    return { position: "relative", height: "100%" };
  }

  /** The explanations the drawn chart needs: gaps, the partial year, the percentile band. */
  get notes(): string[] {
    const notes: string[] = [];
    if (this.points.some((point) => point.belowFloor)) notes.push(this.$t("mira.trend.gapNote").toString());
    if (this.points.some((point) => point.partial)) notes.push(this.$t("mira.trend.partialNote").toString());
    // Names the thin median line against the mean line, since the median is drawn to everyone.
    if (this.hasMedianLine) notes.push(this.$t("mira.trend.medianLineNote").toString());
    if (this.hasBand) notes.push(this.$t("mira.trend.bandNote").toString());
    if (this.periodSpan) notes.push(this.$t("mira.trend.periodNote").toString());
    return notes;
  }

  /** Whether any point carries both percentiles, so a band can be drawn. Never on a total. */
  get hasBand(): boolean {
    return !this.isTotal && this.showPercentiles && this.points.some((point) => point.p15 !== null && point.p85 !== null);
  }

  /** Re-creates the chart when its axis labels change (see {@link chartAxisKey}). */
  get chartKey(): string {
    return chartAxisKey(this.points.map((point) => point.label));
  }

  get chartData() {
    const points = this.points;
    const labels = points.map((point) => point.label);

    // A total draws as bars: a sum per month is compared bar to bar. The partial year is hollow (surface
    // fill, outlined), and a gap draws no bar; no band and no median line.
    if (this.isTotal) {
      const bars = {
        type: "bar",
        label: this.title,
        order: 0,
        data: points.map((point) => point.value),
        backgroundColor: points.map((point) => (point.partial ? this.surfaceColor : this.lineColor)),
        borderColor: this.lineColor,
        borderWidth: points.map((point) => (point.partial ? 1 : 0)),
      };
      return { labels, datasets: [bars] };
    }

    const line = {
      label: this.title,
      // Chart.js draws a lower `order` on top: the line must sit above the band and the median line.
      order: 0,
      data: points.map((point) => point.value),
      borderColor: this.lineColor,
      borderWidth: 2,
      spanGaps: false,
      fill: false,
      tension: 0,
      // The current year to date is a hollow ring in the line colour, on the solid line, so it reads as
      // open; every other point is a filled dot.
      pointRadius: 3,
      pointHoverRadius: 5,
      pointBorderWidth: 2,
      pointBorderColor: this.lineColor,
      pointBackgroundColor: points.map((point) => (point.partial ? this.surfaceColor : this.lineColor)),
    };

    const datasets: Record<string, unknown>[] = [];

    if (this.hasBand) {
      // The band is two datasets: the upper (P85), then the lower (P15) filling up to it. Both break over
      // gaps, and neither takes a tooltip (see the filter in the options). Drawn under the median line.
      datasets.push({
        label: "p85",
        order: 4,
        data: points.map((point) => point.p85),
        borderWidth: 0,
        pointRadius: 0,
        // No hover dot on the band: only the line answers the pointer.
        pointHoverRadius: 0,
        pointHitRadius: 0,
        spanGaps: false,
        fill: false,
      });
      datasets.push({
        label: "p15",
        order: 3,
        data: points.map((point) => point.p15),
        borderWidth: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
        pointHitRadius: 0,
        spanGaps: false,
        backgroundColor: themed(COLOR_MIRA_TREND_BAND_FILL, COLOR_MIRA_TREND_BAND_FILL_DARK),
        fill: "-1",
      });
    }

    if (this.hasMedianLine) {
      // The median as a thin light line inside the band, above it and below the main mean line.
      datasets.push({
        label: "median",
        order: 2,
        data: points.map((point) => trendMedianValue(point)),
        borderColor: this.medianColor,
        borderWidth: 1,
        spanGaps: false,
        fill: false,
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
        pointHitRadius: 0,
      });
    }

    // The line is last, so `chartOptions` can read the main dataset as the final index for its tooltip.
    datasets.push(line);
    return { labels, datasets };
  }

  get chartOptions() {
    const points = this.points;
    const mainIndex = this.chartData.datasets.length - 1;
    const formatValue = this.formatValue;
    const context = this.context;
    const t = (key: string, params: string[]) => context.t(key, params);
    const metricId = this.metricId;
    const showMedian = this.hasMedianLine;
    const isTotal = this.isTotal;
    const span = this.periodSpan;
    const periodColor = themed(COLOR_MIRA_TREND_PERIOD, COLOR_MIRA_TREND_PERIOD_DARK);

    return {
      responsive: true,
      maintainAspectRatio: false,
      // A short transition when the data really changes (a grain switch, another tile). It no longer
      // replays on every render: the screens pass a cached formatter (`activeTrendFormatter`).
      animation: { duration: 400 },
      interaction: { mode: "index", intersect: false },
      scales: {
        // Bars sit in a line chart here, whose category axis has no offset: without it the first and last
        // bar of a total are cut in half at the chart's edges.
        x: { offset: isTotal },
        y: {
          // From zero, so a change of a few days does not read as a cliff (an overrun can still go below).
          beginAtZero: true,
          // The figure's own unit on the axis, so a share reads "20 %" and not "0.2"; whole hours for an
          // hours duration.
          ...trendValueAxis(formatValue),
        },
      },
      plugins: {
        legend: { display: false },
        // The chosen period, shaded behind the line (chartjs-plugin-annotation, registered in main.ts).
        annotation: {
          annotations: span
            ? {
                period: {
                  type: "box",
                  xMin: span.first - 0.5,
                  xMax: span.last + 0.5,
                  backgroundColor: periodColor,
                  borderWidth: 0,
                  // Under everything, grid included; the band is see-through, so the shading shows in it.
                  drawTime: "beforeDraw",
                },
              }
            : {},
        },
        tooltip: {
          // One series, so no colour square: it only crowded the value.
          displayColors: false,
          // Only the line, never the band datasets.
          filter: (item: { datasetIndex: number }) => item.datasetIndex === mainIndex,
          callbacks: {
            label: (item: { dataIndex: number }) => {
              const point = points[item.dataIndex];
              // The drawn value (the mean, or the total) first, in its pre-formatted text.
              return point && point.value !== null ? point.valueText ?? "" : "";
            },
            afterLabel: (item: { dataIndex: number }) => {
              const point = points[item.dataIndex];
              if (!point) {
                return [];
              }
              // The cases in the figure's own unit (S1); none where they are the value itself (Projekte).
              const lines = caseCountIsValue(metricId) ? [] : [t("mira.trend.tooltipBasis", [casesText(metricId, point.basis, context)])];
              // The median beside the mean, where the median line is drawn and the point carries it.
              if (showMedian && point.median !== null) {
                lines.push(t("mira.trend.tooltipMedian", [formatValue(point.median)]));
              }
              return lines;
            },
          },
        },
      },
    };
  }
}
